import api from './api';
import { cache } from '../utils/cache';
import type { Machine, MachineFormData } from '../types/machine';

interface MachineDTO {
  id: string;
  name: string;
  status?: string;
  is_active?: boolean;
  isActive?: boolean;
  is_sampel_roaster?: boolean;
  isSampelRoaster?: boolean;
  notes?: string;
  internal_notes?: string;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
}

type MachineListResponse = MachineDTO[] | { data: MachineDTO[] };

const mapDtoToMachine = (data: MachineDTO): Machine => ({
  id: data.id,
  name: data.name,
  isActive: data.status ? data.status === 'active' : (data.is_active ?? data.isActive ?? true),
  isSampelRoaster: data.is_sampel_roaster ?? data.isSampelRoaster ?? false,
  notes: data.notes ?? data.internal_notes,
  createdAt: data.created_at ?? data.createdAt,
  updatedAt: data.updated_at ?? data.updatedAt,
});

const getListPayload = (payload: MachineListResponse): MachineDTO[] => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  return [];
};

export const machineService = {
  getAll: async (): Promise<Machine[]> => {
    return cache.fetch('machines_all', async () => {
      const response = await api.get<MachineListResponse>('/machines');
      const items = getListPayload(response.data);
      return items.map(mapDtoToMachine);
    });
  },

  getById: async (id: string): Promise<Machine> => {
    return cache.fetch(`machines_${id}`, async () => {
      const response = await api.get<MachineDTO>(`/machines/${id}`);
      return mapDtoToMachine(response.data);
    });
  },

  create: async (data: MachineFormData): Promise<Machine> => {
    const payload = {
      name: data.name,
      status: data.isActive ? 'active' : 'inactive',
      is_sampel_roaster: data.isSampelRoaster ?? false,
      notes: data.notes,
    };
    const response = await api.post<MachineDTO>('/machines', payload);
    cache.invalidate('machines_all');
    return mapDtoToMachine(response.data);
  },

  update: async (id: string, data: MachineFormData): Promise<Machine> => {
    const payload = {
      name: data.name,
      status: data.isActive ? 'active' : 'inactive',
      is_sampel_roaster: data.isSampelRoaster ?? false,
      notes: data.notes,
    };
    const response = await api.put<MachineDTO>(`/machines/${id}`, payload);
    cache.delete(`machines_${id}`);
    cache.invalidate('machines_all');
    return mapDtoToMachine(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/machines/${id}`);
    cache.delete(`machines_${id}`);
    cache.invalidate('machines_all');
  },
};
